import React, { useState, useEffect, useMemo } from 'react'
import { Box, Modal } from '@mui/material'
import AddOrganizationModal from '../components/Organization/AddOrganizationModal'
import EditOrganizationModal from '../components/Organization/EditOrganizationModal'
import DeleteModal from '../components/modal/DeleteModal'
import HeavyTable from '../components/table/HeavyTable'
import {
    makeSureIsArray, makeOptions, checkIfAnySelectedRowIs, resetAll,
    changeViewOptions, changeRowsPerPage, changeFiltered, changeSearch, loadData, cancelAction
} from '../functions/general'
import { showWarningSwal } from '../functions/alert'
import { getOrganizations } from '../functions/getData'
import { deleteOrganization } from '../functions/deleteData'
import { showNotification } from '../functions/snackbar'
import ReusableButton from '../components/general/ReusableButton'
import LoadingBackdrop from '../components/general/LoadingBackdrop'
import NoData from '../components/general/NoData'
import Loader from '../components/general/Loader'
import { useSnackbar } from 'notistack'
import { isAdmin } from '../functions/checkrole'
import PageHeader from '../components/ui/PageHeader'
import TableCard from '../components/ui/TableCard'
import TableToolbar from '../components/ui/TableToolbar'
import SearchInput from '../components/ui/SearchInput'
import { useLegacyFilters } from '../components/ui/useFilterDrawer'
const organizationHeader = [
    { id: 'name', label: 'Organization Name' },
    { id: 'country', label: 'Country' },
    { id: 'email', label: 'Email' },
    { id: 'status', label: 'Status' },
    { id: 'address', label: 'Address' },
    { id: 'phone', label: 'Phone Number' },
    { id: 'users', label: 'No. of Partners' },
    { id: 'person_incharge', label: 'Person-in-charge' },
    { id: 'billing_address', label: 'Billing Address' },
    { id: 'created_by', label: 'Created By' },
    { id: 'last_modified_by', label: 'Last Modified By' },
]
const initialParams = '?limits=10'
export default function Organization() {
    document.title = 'Organization'
    const [header, setHeader] = useState(organizationHeader)
    const [organizationTable, setOrganizationTable] = useState()
    const [filtered, setFiltered] = useState(isAdmin() ? [
        { label: 'Country', state: '', key: 'country_id', header: 'countries' }
    ] : [])
    const [filterOptions, setFilterOptions] = useState()
    const [viewOptions, setViewOptions] = useState([
        { label: "Status", state: true, key: 'status' },
        { label: "Address", state: true, key: 'address' },
        { label: "Phone Number", state: true, key: 'phone' },
        { label: 'No. of Partners', state: true, key: 'users' },
        { label: 'Person-in-charge', state: true, key: 'person_incharge' },
        { label: 'Billing Address', state: true, key: 'billing_address' },
        { label: "Created By", state: true, key: 'created_by' },
        { label: 'Last Modified By', state: true, key: 'last_modified_by' }
    ])
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [search, setSearch] = useState('')
    const [params, setParams] = useState(initialParams)
    const [loading, setLoading] = useState(false)
    const [loadingTable, setLoadingTable] = useState(false)
    const [firstLoaded, setFirstLoaded] = useState(false)
    const [id, setId] = useState('')
    const [selected, setSelected] = useState([])
    const [selecting, setSelecting] = useState([])
    const [onDelete, setOnDelete] = useState(false)
    const [onAddOrganization, setOnAddOrganization] = useState(false)
    const [onEditOrganization, setOnEditOrganization] = useState(false)
    const cancelDelete = () => cancelAction(setOnDelete, setId)
    const cancelEdit = () => cancelAction(setOnEditOrganization, setId)
    const { enqueueSnackbar, closeSnackbar } = useSnackbar()
    const afterFunction = (cancel, data) => {
        if (data.status === 200) {
            resetAll(initialParams, filtered, setParams, setSearch, setFiltered)
            getData(initialParams, signal)
            cancel()
            showNotification('success', data.message, enqueueSnackbar, closeSnackbar)
            setSelected([])
            setSelecting([])
        }
    }

    const deleteFunction = () => {
        deleteOrganization({ id: id === '' ? selected : [id] }).then(d => {
            afterFunction(cancelDelete, d)
        }).catch(e => console.log(e))
    }
    const getData = (params, signal) => {
        setSelected([])
        setSelecting([])
        loadData(setLoadingTable, setFirstLoaded, getOrganizations, params, setOrganizationTable, setFilterOptions, false, signal)
    }
    const onChangeViewOptions = value => changeViewOptions(organizationHeader, setViewOptions, setHeader, value)
    const onChangeRowsPerPage = value => {
        changeRowsPerPage(value, rowsPerPage, filtered, search, signal, setRowsPerPage, setParams, getData)
    }
    const onChangeFiltered = value => {
        changeFiltered(value, filtered, search, rowsPerPage, signal, setFiltered, setParams, getData)
    }
    const onChangeSearch = value => {
        changeSearch(value, search, filtered, rowsPerPage, signal, setSearch, setParams, getData)
    }
    const onChangePage = pageNo => getData(`${params}&page=${pageNo}`, signal)
    const controller = useMemo(() => new AbortController(), [])
    const signal = controller.signal
    useEffect(() => {
        getData(initialParams, signal)
        return () => controller.abort()
    }, [controller, signal])
    const onClickMassDelete = () => {
        return organizationTable && checkIfAnySelectedRowIs(selected, organizationTable.data, 'deleted')
            ? showWarningSwal('Please select organizations that aren\'t already deleted to delete')
            : setOnDelete(true)
    }
    const legacyFilters = useLegacyFilters({
        filtered,
        filterOptions: filterOptions && filtered.map(f => makeOptions(filterOptions, f.header)),
        onChangeFiltered,
    })
    const total = organizationTable?.total ?? organizationTable?.meta?.total
    return (
        <Box className='wrapperBox simcc-page'>
            <LoadingBackdrop loading={loading} />
            <PageHeader
                title='Organizations'
                actions={
                    <>
                        {isAdmin() && <>
                            <ReusableButton variant="primary" text='Add Organization' onClick={() => setOnAddOrganization(true)} iconType='add' />
                            <ReusableButton variant="danger" text='Mass Delete' onClick={() => onClickMassDelete()} iconType='delete' />
                        </>}
                    </>
                }
            />
            <TableCard
                toolbar={
                    <TableToolbar
                        search={<SearchInput state={search} setState={onChangeSearch} placeholder="Search name, email…" ariaLabel={"Search only in \"Name\", \"Email\", \"Address\", \"Person In Charge\" and \"Phone Number\" column"} />}
                        onOpenFilters={filterOptions ? legacyFilters.openDrawer : undefined}
                        filtersOpen={legacyFilters.isOpen}
                        filterCount={legacyFilters.count}
                        meta={total !== undefined && `${Number(total).toLocaleString()} organizations`}
                    />
                }
                chips={legacyFilters.chips}
            >
                {loadingTable && <Loader height={600} />}
                {!loadingTable && organizationTable && makeSureIsArray(organizationTable.data).length &&
                    <HeavyTable bare onChangeRowsPerPage={onChangeRowsPerPage} headers={header} list={organizationTable} title='organization' rowsPerPage={rowsPerPage}
                        setOnDelete={setOnDelete} onChangePage={onChangePage} fixed={['name', 'country', 'email']}
                        selecting={selecting} setSelecting={setSelecting} selected={selected} setSelected={setSelected} setId={setId}
                        setOnEdit={setOnEditOrganization}
                    />}
                {!loadingTable && firstLoaded && organizationTable && !makeSureIsArray(organizationTable.data).length && <NoData height={600} />}
            </TableCard>
            {legacyFilters.drawer}
            <Modal open={onDelete} onClose={() => cancelDelete()}>
                <>
                    {Boolean(organizationTable) && <DeleteModal table={organizationTable} id={id} selected={selected}
                        deleteFunction={deleteFunction} cancelDelete={cancelDelete} />}
                </>
            </Modal>
            <Modal open={onAddOrganization} onClose={() => setOnAddOrganization(false)}>
                <>
                    <AddOrganizationModal setLoading={setLoading} setOnAddOrganization={setOnAddOrganization}
                        afterAddingOrganization={afterFunction} />
                </>
            </Modal>
            <Modal open={onEditOrganization} onClose={() => cancelEdit()}>
                <>
                    {organizationTable && <EditOrganizationModal setLoading={setLoading} table={organizationTable} id={id}
                        afterEditingOrganization={afterFunction} cancelEdit={cancelEdit} />}
                </>
            </Modal>
        </Box>
    )
}